import * as React from 'react'

import {
  InlineEditTextField,
  type InlineEditTextFieldStatus,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabelEditableField } from '@webapp/modules/purchasing/features/edit-po-disclaimer-label/models'
import messages from './messages'

export interface IEditablePODisclaimerLabelTextFieldProps {
  value: string
  /** Which label property this cell edits — one View, rendered twice per row */
  field: PODisclaimerLabelEditableField
  status: InlineEditTextFieldStatus
  onChange: (value: string) => void
  /** Commit the current value (fired on blur and Enter) */
  onCommit: () => void
  /** Revert to the persisted value (fired on Escape) */
  onEscape: () => void
}

export const EditablePODisclaimerLabelTextField = ({
  value,
  field,
  status,
  onChange,
  onCommit,
  onEscape,
}: IEditablePODisclaimerLabelTextFieldProps) => {
  const intl = useProcurifyIntl()

  const fieldMessages =
    field === 'description'
      ? {
          placeholder: messages.descriptionPlaceholder,
          ariaLabel: messages.descriptionAriaLabel,
          idle: messages.descriptionStatusIconIdleAriaLabel,
          pending: messages.descriptionStatusIconPendingAriaLabel,
          resolved: messages.descriptionStatusIconResolvedAriaLabel,
          rejected: messages.descriptionStatusIconRejectedAriaLabel,
        }
      : {
          placeholder: messages.contentPlaceholder,
          ariaLabel: messages.contentAriaLabel,
          idle: messages.contentStatusIconIdleAriaLabel,
          pending: messages.contentStatusIconPendingAriaLabel,
          resolved: messages.contentStatusIconResolvedAriaLabel,
          rejected: messages.contentStatusIconRejectedAriaLabel,
        }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onCommit()
    } else if (e.key === 'Escape') {
      onEscape()
    }
  }

  return (
    <InlineEditTextField
      value={value}
      status={status}
      onChange={onChange}
      onBlur={onCommit}
      onKeyDown={handleKeyDown}
      placeholder={intl.formatMessage(fieldMessages.placeholder)}
      ariaLabel={intl.formatMessage(fieldMessages.ariaLabel)}
      statusAriaLabels={{
        idle: intl.formatMessage(fieldMessages.idle),
        pending: intl.formatMessage(fieldMessages.pending),
        resolved: intl.formatMessage(fieldMessages.resolved),
        rejected: intl.formatMessage(fieldMessages.rejected),
      }}
    />
  )
}

EditablePODisclaimerLabelTextField.displayName =
  'EditablePODisclaimerLabelTextField'
